import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Wand2, Sparkles, ArrowRight, Zap, Palette, Image } from 'lucide-react'

export function ImageGeneratorPreview() {
  useEffect(() => {
    // SEO优化：设置页面特定的meta标签
    document.title = 'AI图像生成器 - 免费在线AI绘画工具 | KeyCore AI Studio'
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', '使用先进的AI技术免费生成高质量图像。支持多种艺术风格，提供专业级的AI绘画工具，无需下载，无需注册即可体验。')
    }

    // Open Graph标签
    const setMetaTag = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement
      if (!meta) {
        meta = document.createElement('meta') as HTMLMetaElement
        meta.setAttribute('property', property)
        document.head.appendChild(meta)
      }
      meta.setAttribute('content', content)
    }

    setMetaTag('og:title', 'AI图像生成器 - 免费在线AI绘画工具')
    setMetaTag('og:description', '使用Flux模型生成高质量AI图像，支持文本到图像转换，提供无限创意可能。')
    setMetaTag('og:image', 'https://ai-studio.keycore.tech/og-image-generator.jpg')
    setMetaTag('og:url', 'https://ai-studio.keycore.tech/imagen')
    setMetaTag('og:type', 'website')
  }, [])

  return (
    <div className="min-h-screen bg-[#0d1117]">
      {/* SEO优化的结构化数据 */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "AI图像生成器",
          "description": "免费在线AI绘画工具，使用先进的AI技术生成高质量图像",
          "url": "https://ai-studio.keycore.tech/imagen",
          "applicationCategory": "DeveloperApplication",
          "operatingSystem": "Web Browser",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "featureList": [
            "文本到图像生成",
            "多种艺术风格",
            "高清图像输出",
            "快速生成"
          ],
          "author": {
            "@type": "Organization",
            "name": "KeyCore AI Studio"
          }
        })}
      </script>

      {/* Header */}
      <header className="relative z-10 px-4 py-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2 text-white hover:text-cyan-300 transition-colors">
            <div className="w-8 h-8 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center">
              <Wand2 className="w-5 h-5" />
            </div>
            <span className="font-bold text-xl">KeyCore AI</span>
          </Link>
          <Link
            to="/auth"
            className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-6 py-2 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            开始体验
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <main className="relative z-10 px-4 py-16">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-cyan-500/20 text-cyan-300 px-4 py-2 rounded-full text-sm font-medium mb-8">
            <Sparkles className="w-4 h-4" />
            <span>AI驱动的图像生成</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            AI图像生成器
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
              无限创意
            </span>
          </h1>

          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            使用最新的AI技术，将您的文字描述转换为精美的图像作品。
            支持多种艺术风格，从写实到抽象，从古典到现代，应有尽有。
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
            <Link
              to="/auth"
              className="group inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 rounded-xl font-semibold text-lg transition-all duration-200 transform hover:scale-105 shadow-2xl"
            >
              <Wand2 className="w-6 h-6" />
              <span>立即开始创作</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-cyan-900/20 hover:bg-cyan-900/30 border border-cyan-900/30 rounded-xl font-semibold text-lg transition-all duration-200 backdrop-blur-sm"
            >
              <span>了解更多</span>
            </Link>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
            <div className="bg-[#161b22] rounded-xl p-6 border border-cyan-900/30 hover:border-cyan-500/50 transition-colors">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-cyan-500/20 rounded-lg mb-4">
                <Zap className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">快速生成</h3>
              <p className="text-gray-400">先进的AI模型，数秒内生成高质量图像</p>
            </div>

            <div className="bg-[#161b22] rounded-xl p-6 border border-cyan-900/30 hover:border-cyan-500/50 transition-colors">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-lg mb-4">
                <Palette className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">多种风格</h3>
              <p className="text-gray-400">从写实到艺术，从古典到现代，满足各种创作需求</p>
            </div>

            <div className="bg-[#161b22] rounded-xl p-6 border border-cyan-900/30 hover:border-cyan-500/50 transition-colors">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-lg mb-4">
                <Image className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">高清输出</h3>
              <p className="text-gray-400">支持多种分辨率输出，满足专业设计需求</p>
            </div>
          </div>

          {/* How it works */}
          <div className="bg-[#161b22] rounded-2xl p-8 border border-cyan-900/30 mb-16">
            <h2 className="text-3xl font-bold text-white mb-8">如何使用</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">1</span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">描述您的想法</h3>
                <p className="text-gray-400">用自然语言详细描述您想要生成的图像</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">2</span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">选择参数</h3>
                <p className="text-gray-400">调整风格、尺寸等参数，定制您的图像</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">3</span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">生成下载</h3>
                <p className="text-gray-400">AI瞬间生成图像，免费下载使用</p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Link
              to="/auth"
              className="inline-flex items-center space-x-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-12 py-5 rounded-2xl font-bold text-xl transition-all duration-300 transform hover:scale-105 shadow-2xl"
            >
              <Wand2 className="w-7 h-7" />
              <span>现在就开始创作</span>
              <ArrowRight className="w-6 h-6" />
            </Link>
          </div>
        </div>
      </main>
    </div>
  )
}
